/*
 * The four states a panel can be in, rendered so they cannot be confused.
 *
 * "Nothing on screen" has to mean exactly one thing. A tab that renders blank for
 * a 404, blank for an empty result and blank while loading is a tab nobody can
 * review: the reader cannot tell a working feature with no data from a route that
 * was never deployed. So each state says which it is, and a failure carries the
 * status code and the message the API actually returned rather than a house
 * euphemism.
 */
import type { ReactNode } from 'react'
import type { Resource } from '../lib/useResource.ts'

interface AsyncStateProps {
  /** What the panel is reading, in the accusative — "историю", "страницы wiki". */
  what: string
  status: Resource<unknown>['status']
  error: Error | null
  httpStatus: number | null
  onRetry?: () => void
  /** Rows of skeleton to show while loading; 0 renders a line of text instead. */
  skeletonRows?: number
}

export function AsyncState({
  what,
  status,
  error,
  httpStatus,
  onRetry,
  skeletonRows = 4,
}: AsyncStateProps) {
  if (status === 'loading') {
    if (skeletonRows <= 0) {
      return (
        <div className="state">
          <div className="muted-2">Загружаем {what}…</div>
        </div>
      )
    }
    return (
      <div className="state" aria-busy="true" aria-label={`Загружаем ${what}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, textAlign: 'left' }}>
          {Array.from({ length: skeletonRows }, (_, index) => (
            <span
              key={index}
              className="skeleton"
              style={{ width: `${92 - index * 11}%`, animationDelay: `${index * 0.09}s` }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (status === 'unavailable') {
    return (
      <div className="state state--pending">
        <div className="state__title">Эндпоинт пока недоступен</div>
        <p>
          Не удалось получить {what}: API ответил {httpStatus ?? 404}. Это либо ещё не развёрнутый
          маршрут, либо база недоступна в текущем скоупе, либо функция выключена для аккаунта —
          снаружи эти три случая не различаются.
        </p>
        {error?.message ? <div className="state__detail">{error.message}</div> : null}
        {onRetry ? (
          <div className="state__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
              Повторить
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="state state--error">
        <div className="state__title">
          Не удалось загрузить {what}
          {httpStatus ? ` · ${httpStatus}` : ''}
        </div>
        {error?.message ? <div className="state__detail">{error.message}</div> : null}
        {onRetry ? (
          <div className="state__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
              Повторить
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  return null
}

interface EmptyProps {
  title: string
  children?: ReactNode
}

/** A genuinely empty result — distinct from every failure above. */
export function Empty({ title, children }: EmptyProps) {
  return (
    <div className="state">
      <div className="state__title">{title}</div>
      {children ? <p>{children}</p> : null}
    </div>
  )
}

/**
 * Render one of: a state placeholder, or the children.
 *
 * Keeps the `status !== 'ready' ? <AsyncState/> : …` ternary out of every panel,
 * which is where the "blank on 404" bug gets reintroduced.
 */
export function Loaded<T>({
  resource,
  what,
  skeletonRows,
  children,
}: {
  resource: Resource<T>
  what: string
  skeletonRows?: number
  children: (data: T) => ReactNode
}) {
  if (resource.status !== 'ready' || resource.data === null) {
    return (
      <AsyncState
        what={what}
        status={resource.status}
        error={resource.error}
        httpStatus={resource.httpStatus}
        onRetry={resource.reload}
        {...(skeletonRows === undefined ? {} : { skeletonRows })}
      />
    )
  }
  return <>{children(resource.data)}</>
}
